import { i18n as _i18n } from "@lingui/core";
import { MktSelectors, formNames } from '@wkda/carlead-creation-form';
import { useAppConfiguration, useSuspiciousMileageCheck } from '@wkda/funnel-components';
import { formatThousand, leaveOnlyNumber } from '@wkda/funnel-utils';
import { AlertBox } from '@wkda/styleguide';
import cx from 'classnames';
import { useCallback, useEffect, useRef } from 'react';
import { useField } from 'react-final-form';
import { useCarleadFieldChangedTracking } from '../../hooks/useCarleadFieldChangedTracking';
import { BaseField } from '../BaseField';
import { selectFieldRenderer } from '../fieldRenderers/selectFieldRenderer';
import { textInputRenderer } from '../fieldRenderers/textInputRenderer';
import { useSyncValues } from '../hooks/useAutoSelectForDataFields';
import { useStateMachineForFields } from '../hooks/useStateMachineForFields';
import { mileageData } from './mileageData';
import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
const SUSPICIOUS_MILEAGE_MESSAGE_DELAY = 1300;
export function CarleadMileage(_ref) {
  let {
    label = _i18n._(
    /*i18n*/
    {
      id: 'carleadCreation.form.label.mileage'
    }),
    placeholder = _i18n._(
    /*i18n*/
    {
      id: 'carleadCreation.form.placeholder.mileage'
    }),
    options = mileageData,
    className,
    onChange,
    ...props
  } = _ref;
  const name = formNames.mileage;
  const {
    isFieldEnabled,
    stateFieldClassNames,
    sendUserSelect
  } = useStateMachineForFields({
    name
  });
  useSyncValues(name);
  return /*#__PURE__*/_jsx(BaseField, {
    label: label,
    name: name,
    MktSelector: MktSelectors.mileage,
    topContainerClassName: stateFieldClassNames,
    disabled: !isFieldEnabled,
    renderFieldComponent: function fieldRenderer(_ref2) {
      let {
        name
      } = _ref2;
      return selectFieldRenderer({
        name,
        placeholder,
        className,
        options: options,
        disabled: !isFieldEnabled,
        onChange(evt) {
          onChange === null || onChange === void 0 || onChange(evt);
          sendUserSelect(evt.target.value);
        }
      });
    },
    ...props
  });
}
export function CarleadMileageText(_ref3) {
  let {
    label = _i18n._(
    /*i18n*/
    {
      id: 'carleadCreation.form.label.mileage',
      message: "Kilometer"
    }),
    placeholder = _i18n._(
    /*i18n*/
    {
      id: 'carleadCreation.form.placeholder.mileage',
      message: "Kilometer ausw\xE4hlen"
    }),
    inputSuffix = _i18n._(
    /*i18n*/
    {
      id: 'selfInspection.distance.km'
    }),
    className,
    pattern = '[0-9]*',
    matchPattern = /^[0-9]*$/,
    parse = v => v,
    format = v => v,
    topContainerClassName,
    ...props
  } = _ref3;
  const name = formNames.mileage;
  const {
    isFieldEnabled,
    stateFieldClassNames,
    sendSelect,
    sendFocus,
    fillInput
  } = useStateMachineForFields({
    name
  });
  const internalPreviousValue = useRef('');
  const {
    trackMileageEditUserEvent,
    trackMileageUserEvent
  } = useMileageTracking(name);
  function onFocus(e) {
    sendFocus();
    internalPreviousValue.current = parse(e.target.value);
  }
  function onBlur(e) {
    if (internalPreviousValue.current !== parse(e.target.value)) {
      trackMileageUserEvent();
    }
    sendSelect(parse(e.target.value));
  }
  const fullTopContainerClassName = cx(topContainerClassName, stateFieldClassNames);
  return /*#__PURE__*/_jsx(BaseField, {
    label: label,
    name: name,
    MktSelector: MktSelectors.mileage,
    topContainerClassName: fullTopContainerClassName,
    disabled: !isFieldEnabled,
    renderFieldComponent: function fieldRenderer(_ref4) {
      let {
        name
      } = _ref4;
      return textInputRenderer({
        id: name,
        name,
        placeholder,
        className,
        disabled: !isFieldEnabled,
        inputMode: 'numeric',
        pattern: pattern !== null && pattern !== void 0 ? pattern : undefined,
        matchPattern: matchPattern !== null && matchPattern !== void 0 ? matchPattern : undefined,
        inputSuffix,
        onFocus,
        onBlur,
        onChange(value) {
          fillInput(parse(value));
          trackMileageEditUserEvent();
        },
        format,
        parse
      });
    },
    ...props
  });
}
export function CarleadMileageFormatted(_ref5) {
  let {
    renderSuspiciousMileageMessage = function () {
      return /*#__PURE__*/_jsx(SuspiciousMileageInfo, {});
    },
    lowerThreshold,
    upperThreshold,
    suspiciousMileageMessageDelay = SUSPICIOUS_MILEAGE_MESSAGE_DELAY,
    ...props
  } = _ref5;
  const {
    locale
  } = useAppConfiguration();
  const {
    input: {
      value: builtDate
    }
  } = useField(formNames.builtDate, {
    subscription: {
      value: true
    }
  });
  const {
    input: {
      value: mileageValue
    }
  } = useField(formNames.mileage, {
    subscription: {
      value: true
    }
  });
  const formatMileage = useCallback(value => formatThousand(locale, value), [locale]);
  const parseMileage = useCallback(value => leaveOnlyNumber(value != null ? String(value) : ''), []);
  const isSuspiciousMileage = useSuspiciousMileageCheck({
    lowerThreshold,
    upperThreshold,
    builtDate,
    delay: suspiciousMileageMessageDelay,
    mileage: mileageValue
  });
  const topContainerClassName = cx({
    'field_has-suspicious-value': isSuspiciousMileage
  });
  return /*#__PURE__*/_jsx(_Fragment, {
    children: /*#__PURE__*/_jsx(CarleadMileageText, {
      format: formatMileage,
      parse: parseMileage,
      pattern: null,
      matchPattern: null,
      topContainerClassName: topContainerClassName,
      renderInfoText: function renderInfoText(text) {
        return /*#__PURE__*/_jsxs(_Fragment, {
          children: [Boolean(text) ? /*#__PURE__*/_jsx("span", {
            className: "font-size-xs d-block mt-1 text-muted",
            children: text
          }) : null, isSuspiciousMileage ? renderSuspiciousMileageMessage() : null]
        });
      },
      ...props
    })
  });
}
export function SuspiciousMileageInfo(_ref6) {
  let {
    suspiciousMileageMessage = _i18n._(
    /*i18n*/
    {
      id: 'selfInspection.error.suspiciousMileage'
    })
  } = _ref6;
  return /*#__PURE__*/_jsx("div", {
    className: "mt-1",
    children: /*#__PURE__*/_jsx(AlertBox, {
      variant: "info",
      design: "complete",
      children: suspiciousMileageMessage
    })
  });
}
function useMileageTracking(fieldName) {
  const trackLeadFieldChanged = useCarleadFieldChangedTracking();
  useEffect(() => {
    const hasMileageCachedEvent = hasEvent(fieldName, 'cache');
    const hasMileageEditUserEvent = hasEvent('mileage_edit', 'cache');
    if (hasMileageCachedEvent && !hasMileageEditUserEvent) {
      trackLeadFieldChanged('mileage_edit', 'cache');
    }
  }, [fieldName, trackLeadFieldChanged]);
  return {
    trackMileageEditUserEvent() {
      if (hasEvent('mileage_edit')) return;
      trackLeadFieldChanged('mileage_edit', 'user');
    },
    trackMileageUserEvent() {
      trackLeadFieldChanged(fieldName, 'user');
    }
  };
}
function hasEvent(name, source) {
  var _ref7;
  const dataLayer = //@ts-expect-error
  (_ref7 = window['dataLayer']) !== null && _ref7 !== void 0 ? _ref7 : [];
  if (dataLayer.length === 0) {
    console.warn("dataLayer not found");
  }
  return dataLayer.some(row => {
    if (row.leadFieldChanged === name) {
      return source ? row.source === source : true;
    }
    return false;
  });
}